import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ControlBarButton } from '@m12s/component-library';
import { playRegular, signOut } from 'lib/icons';
import { UtilityMenu } from 'components/UtilityMenu';
import useSubmitLaborTicket from 'components/LaborTicket/useSubmitLaborTicket';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import { getIsChangeActivitiesLoading } from 'lib/selectors/getIsChangeActivitiesLoading';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { getMachine } from 'lib/selectors/getMachine';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import { CategorizeDowntime } from './CategorizeDowntime';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  Scrollable,
} from '../styled';

/**
 * Control bar component for paused job state.
 *
 * This component is rendered when:
 * - Operator is signed in
 * - Job is currently running
 * - Job is in paused state
 * - System is NOT in ERP-driven mode
 *
 * Available actions:
 * - Resume Job - Resumes the paused job by changing activities back to the latest activity
 * - Categorize Downtime - Categorize downtime intervals
 * - Sign Out - Log out the current operator (simple logout when paused)
 *
 * The resume action is disabled while activity changes are loading to prevent
 * duplicate requests.
 *
 * @returns {JSX.Element} Control bar with resume, downtime categorization, and sign out options
 */
const ActiveJobPaused = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const isChangeActivitiesLoading = useSelector(getIsChangeActivitiesLoading);
  const latestActivity = useSelector(getLatestActivity);
  const requestLaborTicketAction = useSubmitLaborTicket();
  const requestOperatorLogout = useOperatorLogout();

  return (
    <ControlBarResponsive id="job-control-bar">
      <UtilityMenu />
      <Scrollable>
        {/* Resume Job button - resumes paused job by changing activities */}
        <ControlBarButton
          id="job-control-resume-job"
          disabled={isChangeActivitiesLoading}
          onClick={() => {
            // Prevent action if already loading
            if (isChangeActivitiesLoading) {
              return null;
            }
            // Resume by changing activities to the latest activity
            return requestLaborTicketAction({
              action: LABOR_TICKET_ACTIONS.CHANGE_ACTIVITIES,
              opts: {
                pendingActivityTypeRef: latestActivity.activityTypeRef,
              },
            });
          }}
        >
          <MobileIcon icon={playRegular} />
          <ResponsiveText
            full={t('resumeJob|full')}
            medium={t('resumeJob|medium')}
            short={t('resumeJob|short')}
          />
        </ControlBarButton>

        {/* Downtime categorization */}
        <CategorizeDowntime history={history} machine={machine} />

        {/* Sign Out button - simple logout when job is paused */}
        <ControlBarButton
          id="signout"
          onClick={() => {
            return requestOperatorLogout();
          }}
        >
          <MobileIcon icon={signOut} />
          <ResponsiveText
            full={t('signout|full')}
            medium={t('signout|medium')}
            short={t('signout|short')}
          />
        </ControlBarButton>
      </Scrollable>
    </ControlBarResponsive>
  );
};

export { ActiveJobPaused };
